import { IosShareOutlined } from '@mui/icons-material'

const NO_FLOOR_GROUP = 'No Floor Assigned'
const NO_BUILDING_GROUP = 'No Building Assigned'
const SKIP_GROUP = '__skip__'

export const bookmarkMessage = () => {
  const { userAgent } = navigator
  const isMac = userAgent.includes('Mac')
  let message = <>Use your browser menu to bookmark this page.</>

  if (/iPhone|iPad|iPod/.test(userAgent)) {
    if (userAgent.includes('Safari') && !userAgent.includes('CriOS')) {
      // iOS 26 has menu button (...) and then More must be selected to see 'Add to Home Screen'
      // Previous versions had a Share button next to the address bar, either at the top of the screen (iPad) or at the bottom of the screen (iPhone)
      message = (
        <>
          Tap Share <IosShareOutlined color="primary" fontSize="small" /> next to the address bar (or tap the menu (...) first), then select 'Add to Home
          Screen' (you may need to select 'More' first).
        </>
      )
    } else if (userAgent.includes('CriOS')) {
      message = (
        <>
          Tap the Share button <IosShareOutlined color="primary" fontSize="small" /> in the address bar at the top of the screen, select More, then select 'Add
          to Home Screen'.
        </>
      )
    }
  } else if (/Android/.test(userAgent)) {
    if (userAgent.includes('Chrome')) {
      message = <>Tap the menu (⋮) and select 'Add to Home Screen'.</>
    }
  } else if (userAgent.includes('Chrome') || userAgent.includes('Edge') || userAgent.includes('Firefox')) {
    message = isMac ? <>Press ⌘ + D to bookmark this page.</> : <>Press Ctrl + D to bookmark this page.</>
  } else if (userAgent.includes('Safari')) {
    message = <>Go to the menu bar and click 'Bookmarks' → 'Add Bookmark'.</>
  }

  return message
}

export const getDevicePriority = (device: IDeviceType): number => {
  const activeAlerts = device.alerts.filter((a) => a.status === 'active')
  const hasUrgentUnresponded = activeAlerts.some((a) => a.tags.includes('urgent') && a.responseHistory.length === 0)
  if (hasUrgentUnresponded) return 1
  const hasWarning = activeAlerts.some((a) => a.tags.includes('warning'))
  if (hasWarning) return 2
  const hasInfo = activeAlerts.some((a) => a.tags.includes('info'))
  if (hasInfo) return 3
  const hasUrgentResponded = activeAlerts.some((a) => a.tags.includes('urgent') && a.responseHistory.length > 0)
  if (hasUrgentResponded) return 4
  return 5
}

export const getAlertIssueType = (alert: IAlertHistory) => {
  if (
    alert.name.includes('Freeze Loss Warning') ||
    alert.name.includes('Low Temperature Warning') ||
    alert.name.includes('Freezer Threshold Warning') ||
    alert.name.includes('Refrigeration Issue Warning') ||
    alert.name.includes('High Ambient Temperature Warning') ||
    alert.name.includes('Wine High Temp Warning') ||
    alert.name.includes('Wine Low Temp Warning')
  )
    return 'temp-warning'
  if (alert.name.includes('Gateway Not Connected')) return 'gateway-offline'
  if (
    alert.name.includes('Shutoff Inactivity') ||
    alert.name.includes('Sensor Unresponsive') ||
    alert.name.includes('Sensor Inactivity') ||
    alert.name.includes('Sensor Powered Off')
  )
    return 'not-communicating'
  if (alert.name.includes('Low Battery')) return 'low-battery'
  if (alert.name.includes('Gateway Has Switch to Battery')) return 'gateway-on-battery'
  return 'generic-alert'
}

export const getGroupSlug = (name: string) =>
  name
    .split('||')
    .filter((part) => part !== SKIP_GROUP)
    .map((part) =>
      part
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    )
    .join('_')

export const groupDevices = ({ newData }: { newData: IDeviceType[] }) => {
  const anyHasBuilding = newData.some((device) => !!device.node.building)
  const devicesWithBuilding = newData.filter((device) => !!device.node.building)
  const devicesWithoutBuildingWithFloor = newData.filter((device) => !device.node.building && !!device.node.floor)
  const devicesWithoutBuildingWithoutFloor = newData.filter((device) => !device.node.building && !device.node.floor)

  const groupedDevices = anyHasBuilding ? devicesWithBuilding : []
  const ungroupedDevices = anyHasBuilding ? [] : devicesWithoutBuildingWithoutFloor

  const addDeviceToFloorGroup = (floors: IFloorGroup[], device: IDeviceType, buildingHasFloor: boolean) => {
    const floorName = buildingHasFloor ? device.node.floor || NO_FLOOR_GROUP : NO_FLOOR_GROUP
    const floorGroup = floors.find((f) => f.name === floorName)

    if (floorGroup) {
      floorGroup.devices.push(device)
      floorGroup.hasAlert = floorGroup.hasAlert || device.activeAlertsCount > 0
      return
    }

    floors.push({
      name: floorName,
      hasAlert: device.activeAlertsCount > 0,
      devices: [device],
    })
  }

  // First pass: group devices by building
  const devicesByBuilding = new Map<string, IDeviceType[]>()
  groupedDevices.forEach((device: IDeviceType) => {
    const buildingName = device.node.building || NO_BUILDING_GROUP
    const existing = devicesByBuilding.get(buildingName) ?? []
    existing.push(device)
    devicesByBuilding.set(buildingName, existing)
  })

  // Second pass: build floor groups per building using per-building floor presence
  const newBuildingGroups: IBuildingGroup[] = []
  devicesByBuilding.forEach((devices, buildingName) => {
    const buildingHasFloor = devices.some((d) => !!d.node.floor)
    const newBuilding: IBuildingGroup = {
      name: buildingName,
      hasAlert: devices.some((d) => d.activeAlertsCount > 0),
      anyHasFloor: buildingHasFloor,
      floors: [],
    }
    devices.forEach((device) => addDeviceToFloorGroup(newBuilding.floors, device, buildingHasFloor))
    newBuildingGroups.push(newBuilding)
  })

  // Devices with floor but no building should be grouped by floor at top level
  const noBuildingDevicesByFloor = new Map<string, IDeviceType[]>()
  devicesWithoutBuildingWithFloor.forEach((device: IDeviceType) => {
    const floorName = device.node.floor || NO_FLOOR_GROUP
    const existing = noBuildingDevicesByFloor.get(floorName) ?? []
    existing.push(device)
    noBuildingDevicesByFloor.set(floorName, existing)
  })

  noBuildingDevicesByFloor.forEach((devices, floorName) => {
    newBuildingGroups.push({
      name: `Floor ${floorName}`,
      hasAlert: devices.some((d) => d.activeAlertsCount > 0),
      anyHasFloor: false,
      floors: [
        {
          name: floorName,
          hasAlert: devices.some((d) => d.activeAlertsCount > 0),
          devices,
        },
      ],
    })
  })

  if (anyHasBuilding && devicesWithoutBuildingWithoutFloor.length > 0) {
    newBuildingGroups.push({
      name: NO_BUILDING_GROUP,
      hasAlert: devicesWithoutBuildingWithoutFloor.some((d) => d.activeAlertsCount > 0),
      anyHasFloor: false,
      floors: [
        {
          name: NO_FLOOR_GROUP,
          hasAlert: devicesWithoutBuildingWithoutFloor.some((d) => d.activeAlertsCount > 0),
          devices: devicesWithoutBuildingWithoutFloor,
        },
      ],
    })
  }

  // Sort and move 'No Building Assigned' to end
  newBuildingGroups.sort((a, b) => {
    if (a.name === NO_BUILDING_GROUP) return 1
    if (b.name === NO_BUILDING_GROUP) return -1
    return a.name.localeCompare(b.name)
  })

  // Sort floors within each building
  newBuildingGroups.forEach((building) => {
    building.floors.sort((a, b) => {
      if (a.name === NO_FLOOR_GROUP) return 1
      if (b.name === NO_FLOOR_GROUP) return -1
      return a.name.localeCompare(b.name)
    })
  })

  return {
    buildingGroups: newBuildingGroups,
    ungroupedDevices,
  }
}
